import { useEffect, useMemo, useState } from 'react'
import GShell from './GShell.jsx'
import { Title, Card, money } from './gui.jsx'
import { useMembersMap, memberName } from './MemberPicker.jsx'
import { watchSales } from '../../lib/sales.js'
import { watchProducts } from '../../lib/products.js'
import { watchRepairs } from '../../lib/repairs.js'
import { watchEmployees } from '../../lib/employees.js'
import { watchVendors } from '../../lib/vendors.js'
import { watchPurchaseOrders } from '../../lib/purchaseOrders.js'

const vals = (o) => Object.values(o || {}).filter(Boolean)
const startOf = (days) => { const d = new Date(); d.setDate(d.getDate() - days); return d.getTime() }

export default function GProReports() {
  const [sales, setSales] = useState({})
  const [products, setProducts] = useState({})
  const [repairs, setRepairs] = useState({})
  const [emps, setEmps] = useState({})
  const [vendors, setVendors] = useState({})
  const [pos, setPos] = useState({})
  const members = useMembersMap()
  useEffect(() => {
    const offs = [watchSales(setSales), watchProducts(setProducts), watchRepairs(setRepairs), watchEmployees(setEmps), watchVendors(setVendors), watchPurchaseOrders(setPos)]
    return () => offs.forEach((o) => o && o())
  }, [])

  const S = vals(sales)
  const sum = (arr) => arr.reduce((s, x) => s + (Number(x.total) || 0), 0)
  const sales1 = sum(S.filter((s) => s.timestamp >= startOf(1)))
  const sales7 = sum(S.filter((s) => s.timestamp >= startOf(7)))
  const sales30 = sum(S.filter((s) => s.timestamp >= startOf(30)))
  const sales365 = sum(S.filter((s) => s.timestamp >= startOf(365)))

  // Unit sales per product (from sale line items).
  const unitsByProduct = useMemo(() => { const m = {}; S.forEach((s) => (s.lines || []).forEach((l) => { if (l.productId) m[l.productId] = (m[l.productId] || 0) + (Number(l.qty) || 0) })); return m }, [sales])
  const P = vals(products)
  const withUnits = P.map((p) => ({ p, units: unitsByProduct[p.id] || 0 }))
  const topSellers = [...withUnits].sort((a, b) => b.units - a.units).slice(0, 5)
  const dead = withUnits.filter((x) => x.units === 0)
  const low = P.filter((p) => Number(p.quantity) <= Number(p.minQuantity || 0) && Number(p.minQuantity || 0) > 0)
  const overstock = P.filter((p) => Number(p.reorderQuantity || 0) > 0 && Number(p.quantity) > Number(p.reorderQuantity) * 3)

  const R = vals(repairs)
  const repairsOpen = R.filter((r) => !['Picked Up', 'Paid'].includes(r.status)).length
  const repairsClosed = R.length - repairsOpen
  const repairRevenue = R.reduce((s, r) => s + (Number(r.parts) || 0) + (Number(r.labor) || 0), 0)

  const salesByEmp = useMemo(() => { const m = {}; S.forEach((s) => { if (s.employeeId) m[s.employeeId] = (m[s.employeeId] || 0) + (Number(s.total) || 0) }); return m }, [sales])
  const poTotal = (po) => Object.values(po.lines || {}).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.cost) || 0), 0)
  const purchasesByVendor = useMemo(() => { const m = {}; vals(pos).forEach((po) => { if (po.vendorId) m[po.vendorId] = (m[po.vendorId] || 0) + poTotal(po) }); return m }, [pos])
  const spendByMember = useMemo(() => { const m = {}; S.forEach((s) => { if (s.memberId) { m[s.memberId] = m[s.memberId] || { total: 0, visits: 0 }; m[s.memberId].total += Number(s.total) || 0; m[s.memberId].visits += 1 } }); return m }, [sales])
  const topMembers = Object.entries(spendByMember).sort((a, b) => b[1].total - a[1].total).slice(0, 5)

  return (
    <GShell>
      <Title title="Reports" subtitle="Rollups from live suite data." />
      <div className="grid md:grid-cols-2 gap-3">
        <Card>
          <H>Sales</H>
          <Line l="Today" r={money(sales1)} /><Line l="Last 7 days" r={money(sales7)} /><Line l="Last 30 days" r={money(sales30)} /><Line l="Last 365 days" r={money(sales365)} />
          <Line l="Total transactions" r={S.length} />
        </Card>
        <Card>
          <H>Inventory</H>
          <div className="text-xs text-gray-500 mb-1">Top sellers</div>
          {topSellers.length ? topSellers.map((x) => <Line key={x.p.id} l={x.p.name || x.p.sku} r={`${x.units} sold`} />) : <p className="text-xs text-gray-400">No sales yet.</p>}
          <Line l="Dead stock (0 sold)" r={dead.length} /><Line l="Low stock" r={low.length} /><Line l="Overstock" r={overstock.length} />
        </Card>
        <Card>
          <H>Repairs</H>
          <Line l="Open" r={repairsOpen} /><Line l="Closed" r={repairsClosed} /><Line l="Revenue (parts+labor)" r={money(repairRevenue)} />
        </Card>
        <Card>
          <H>Employees</H>
          {vals(emps).length ? vals(emps).map((e) => { const s = salesByEmp[e.id] || 0; return <Line key={e.id} l={e.name} r={`${money(s)} · comm ${money(s * (Number(e.commissionPct) || 0) / 100)}`} /> }) : <p className="text-xs text-gray-400">No employees.</p>}
        </Card>
        <Card>
          <H>Vendors (purchases)</H>
          {vals(vendors).length ? vals(vendors).map((v) => <Line key={v.id} l={v.name} r={money(purchasesByVendor[v.id] || 0)} />) : <p className="text-xs text-gray-400">No vendors.</p>}
        </Card>
        <Card>
          <H>Members (top spenders)</H>
          {topMembers.length ? topMembers.map(([id, d]) => <Line key={id} l={memberName(members, id)} r={`${money(d.total)} · ${d.visits} visit${d.visits === 1 ? '' : 's'}`} />) : <p className="text-xs text-gray-400">No member sales yet.</p>}
        </Card>
      </div>
    </GShell>
  )
}
function H({ children }) { return <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: 'var(--ge-primary)' }}>{children}</div> }
function Line({ l, r }) { return <div className="flex items-center justify-between border-b py-0.5 text-sm" style={{ borderColor: 'rgba(0,0,0,0.05)' }}><span className="text-gray-600 truncate">{l}</span><span className="font-bold shrink-0" style={{ color: 'var(--ge-primary)' }}>{r}</span></div> }
